import { systemTimeZone } from "@app/core";
import { GoogleCalendarClient } from "@app/storage";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { rehydrateStores } from "../lib/snapshot.js";
import { reconcileDuplicateTasks } from "../lib/dedupe-tasks.js";
import { withSyncLane } from "../lib/sync-lane.js";
import { accountGeneration } from "../lib/account-session.js";
import { pullCalendar, pushCalendar, type ReconcileDeps } from "../features/google-sync/reconcile.js";
import type { CalendarApi } from "../features/google-sync/provider.js";
import { tr } from "../lib/tr.js";
import { useAuthStore } from "./auth.store.js";
import { useGoogleAuthStore } from "./googleAuth.store.js";
import { serverSyncedThisSession, useSyncStore } from "./sync.store.js";
import { toast } from "./toast.store.js";

interface SyncOptions {
  /** Suppress success/error toasts (auto-runs). Conflicts + status still set. */
  silent?: boolean;
  /** Injected API for tests; defaults to a real GoogleCalendarClient. */
  client?: CalendarApi;
}

interface GoogleSyncState {
  busy: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  lastConflicts: number;
  syncNow: (opts?: SyncOptions) => Promise<void>;
}

type SetState = (partial: Partial<GoogleSyncState>) => void;

class AccountChanged extends Error {
  constructor() {
    super("account changed before Google sync");
  }
}

/**
 * Client-driven Google Calendar reconcile orchestrator. Mirrors `sync.store.ts`:
 * an overlap guard, a per-(bound)-calendar pull→push pass, and a rehydrate ONLY
 * when something actually landed locally — so its own writes don't loop back
 * through `useGoogleSyncReconciler`'s change-debounced trigger.
 *
 * The whole pass runs inside the shared SYNC LANE (`lib/sync-lane.ts`) so it can
 * never interleave with multi-device sync over the same rows. The cursor fields
 * it writes (`sync_token`/`last_*_at`) are device-local and stripped from backups
 * + multi-device sync, so they never propagate.
 */
export const useGoogleSyncStore = create<GoogleSyncState>((set, get) => ({
  busy: false,
  lastSyncAt: null,
  lastError: null,
  lastConflicts: 0,

  async syncNow(opts) {
    if (get().busy) return; // overlap guard
    if (!useGoogleAuthStore.getState().connected) return;
    set({ busy: true });
    try {
      const generation = await ensureServerSyncFirst();
      const assertSameAccount = () => {
        if (accountGeneration() !== generation) throw new AccountChanged();
      };
      await withSyncLane(async () => {
        // The account may have changed while the server-first pass or our lane
        // slot was awaited. Never let that pass run under the new account before
        // its own server ordering has completed; the next trigger will retry.
        assertSameAccount();
        await runPass(opts, set, assertSameAccount);
        assertSameAccount();
      });
    } catch (e) {
      if (!(e instanceof AccountChanged)) throw e;
    } finally {
      set({ busy: false });
    }
  },
}));

/** One-shot per ACCOUNT: the FIRST Google pass waits for a multi-device sync
 *  when the user has a sync account and none has completed for it yet.
 *
 *  A device offline for weeks can hold a STALE link generation: its derived
 *  event id then points at a tombstoned event, and the sanctioned fallback mints
 *  a randomly-named duplicate — the exact race the derived-id scheme exists to
 *  prevent. Pulling the current `link_rev` from the server first closes it.
 *  Nothing else orders the two engines' first passes (both debounce into the
 *  FIFO lane in arrival order).
 *
 *  Scoped by ACCOUNT GENERATION, not by page lifetime. A page-lifetime flag was
 *  consumed by passes that ran while signed OUT, and neither it nor
 *  `sessionSyncedAt` reset on sign-out — so signing in (or switching accounts)
 *  mid-session left the new account's first Google pass running BEFORE its first
 *  server pull, which is precisely the race above.
 *
 *  Availability over the edge case: attempted ONCE per account — if the server
 *  is down the Google pass proceeds anyway rather than blocking calendar sync.
 *  If the server pass is merely already RUNNING, its lane slot predates ours, so
 *  the FIFO still orders us after it. */
let firstPassOrderedGen: number | null = null;
async function ensureServerSyncFirst(): Promise<number> {
  const generation = accountGeneration();
  if (firstPassOrderedGen === generation) return generation;
  // Signed out: there is no server pass to order — and consuming the one-shot
  // here would skip the ordering entirely for an account that signs in later.
  if (!useAuthStore.getState().accessToken) return generation;
  firstPassOrderedGen = generation;
  if (serverSyncedThisSession()) return generation;
  try {
    await useSyncStore.getState().syncNow({ silent: true });
  } catch {
    /* unreachable server must not block Google sync — proceed */
  }
  return generation;
}

async function runPass(
  opts: SyncOptions | undefined,
  set: SetState,
  assertSameAccount: () => void,
): Promise<void> {
  assertSameAccount();
  const services = getServices();
  const deps: ReconcileDeps = {
    // No link-change recorder and no push cursor: `setRemoteLink` bumps
    // `link_rev` AND `mut` in the same durable write as the identity, and the
    // per-row `mut > acked` facts ARE the queues for both destinations. Nothing
    // has to be remembered across the pass, so a crash mid-pass cannot lose it.
    tasks: services.tasks,
    recurrence: services.recurrence,
    recurrenceOverrides: services.recurrenceOverrides,
    calendars: services.calendars,
  };
  const api: CalendarApi =
    opts?.client ??
    new GoogleCalendarClient(
      () => useGoogleAuthStore.getState().getAccessToken(),
      () => useGoogleAuthStore.getState().refreshAccess(),
    );
  const now = new Date();
  const floatingTimeZone = systemTimeZone();
  let changed = false;
  let conflicts = 0;
  let failures = 0;
  let errored: string | null = null;

  // Opt-in console diagnostics: localStorage.gsyncDebug = "1". Helps localize
  // "synced but nothing moved" — shows bound calendars + per-stage counts.
  const debug = typeof localStorage !== "undefined" && localStorage.getItem("gsyncDebug") === "1";

  const bound = (await services.calendars.listActive()).filter(
    (c) => c.external_source === "google" && c.sync_direction !== "off" && c.external_calendar_id,
  );
  assertSameAccount();
  if (debug) {
    console.info(
      "[gsync] bound calendars:",
      bound.map((c) => ({
        id: c.id,
        gcal: c.external_calendar_id,
        dir: c.sync_direction,
        token: c.sync_token,
      })),
    );
  }

  // No format-migration resync: the encoding is versioned (`tcVer`/`tcState`)
  // and old events upgrade LAZILY via the write-back when they're next touched,
  // so we never clear a sync token (the old loop trigger).
  for (const cal of bound) {
    let syncToken = cal.sync_token;
    let lastSyncedAt = cal.last_synced_at;
    try {
      if (cal.sync_direction === "pull" || cal.sync_direction === "both") {
        const r = await pullCalendar(api, cal, deps, now, { floatingTimeZone });
        assertSameAccount();
        changed = changed || r.changed;
        conflicts += r.conflicts;
        syncToken = r.syncToken;
        lastSyncedAt = r.lastSyncedAt;
        if (debug) console.info("[gsync] pull", cal.external_calendar_id, r.stats);
      }
      if (cal.sync_direction === "push" || cal.sync_direction === "both") {
        // No pre-inbound snapshot and no skip set: the push re-reads storage
        // itself, so it sees the links the pull just created or healed, and the
        // pull's applies are self-acknowledged so they simply are not dirty.
        // Pushing from a stale snapshot is what created second copies of
        // already-synced events. Per-task acknowledgements replace the old
        // `last_push_at` cursor: a failure keeps only ITS row dirty.
        const r = await pushCalendar(api, cal, deps, now, { floatingTimeZone });
        assertSameAccount();
        changed = changed || r.changed;
        conflicts += r.conflicts;
        failures += r.failures;
        if (debug) console.info("[gsync] push", cal.external_calendar_id, r.stats);
      }
      // Persist the device-local cursor through the CURSOR-ONLY writer — never
      // a whole-row `update({...cal})`. `cal` was read at PASS START and the pass
      // is minutes of network IO; a whole-row write from that stale copy reverted
      // any calendar edit the user made meanwhile (rename, hidden, sync
      // direction) AND un-dirtied it (the stale `updated_at` travels too), so the
      // revert never synced anywhere. Cursor columns never bump `updated_at` or
      // `mut`, so a poll doesn't mark the calendar dirty for either engine.
      assertSameAccount();
      await services.calendars.setSyncCursor(cal.id, {
        sync_token: syncToken,
        last_synced_at: lastSyncedAt,
      });
      assertSameAccount();
    } catch (e) {
      if (e instanceof AccountChanged) throw e;
      // Name the calendar so a 404 etc. is actionable ("which calendar?").
      const msg = e instanceof Error ? e.message : String(e);
      errored = `${cal.name}: ${msg}`;
      if (debug) console.warn("[gsync] error on", cal.external_calendar_id, e);
    }
  }

  // Collapse any rows that ended up pointing at the SAME remote event (the
  // residue of the pre-fix duplication bug, or of a restore over live data).
  // Purely local and non-destructive to Google — see `lib/dedupe-tasks.ts`.
  assertSameAccount();
  const healed = bound.length > 0 ? await reconcileDuplicateTasks() : 0;
  assertSameAccount();
  if (healed > 0) {
    changed = true;
    if (debug) console.info("[gsync] healed duplicate task rows:", healed);
  }

  if (changed) {
    assertSameAccount();
    await rehydrateStores();
    assertSameAccount();
  }
  set({ lastSyncAt: now.toISOString(), lastError: errored, lastConflicts: conflicts });
  if (!opts?.silent) {
    if (errored) toast.error(tr("toast.google.syncFailed", { error: errored }));
    else if (failures > 0) {
      toast.info(
        tr("toast.google.syncedWithFailures", {
          count: failures,
          noun: tr(failures === 1 ? "ics.item" : "ics.items"),
        }),
      );
    } else toast.success(tr("toast.google.synced"));
  }
  if (conflicts > 0 && !opts?.silent) {
    toast.info(
      tr("toast.google.conflictsResolved", {
        count: conflicts,
        noun: tr(conflicts === 1 ? "common.conflict" : "common.conflicts"),
      }),
    );
  }
}
